import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FirebaseArticleRepository from '../api/FirebaseArticleRepository';
import GetArticlesUseCase from '../use-cases/GetArticlesUseCase';
import CreateArticleUseCase from '../use-cases/CreateArticleUseCase';
import UpdateArticleUseCase from '../use-cases/UpdateArticleUseCase';
import DeleteArticleUseCase from '../use-cases/DeleteArticleUseCase';
import GetArticleByIdUseCase from '../use-cases/GetArticleByIdUseCase';

const ARTICLES_LIMIT = 10;

const useArticlesViewModel = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const lastVisibleArticleId = useRef(null);
  const hasMore = useRef(true);

  // Instantiate repositories and use cases
  const articleRepository = useMemo(() => new FirebaseArticleRepository(), []);
  const getArticlesUseCase = useMemo(() => new GetArticlesUseCase(articleRepository), [articleRepository]);
  const createArticleUseCase = useMemo(() => new CreateArticleUseCase(articleRepository), [articleRepository]);
  const updateArticleUseCase = useMemo(() => new UpdateArticleUseCase(articleRepository), [articleRepository]);
  const deleteArticleUseCase = useMemo(() => new DeleteArticleUseCase(articleRepository), [articleRepository]);
  const getArticleByIdUseCase = useMemo(() => new GetArticleByIdUseCase(articleRepository), [articleRepository]);

  const loadInitialArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedArticles = await getArticlesUseCase.execute(ARTICLES_LIMIT);
      setArticles(fetchedArticles);
      lastVisibleArticleId.current = fetchedArticles.length > 0 ? fetchedArticles[fetchedArticles.length - 1].id : null;
      hasMore.current = fetchedArticles.length === ARTICLES_LIMIT;
    } catch (err) {
      console.error("Error loading initial articles:", err);
      setError("Failed to load articles.");
    } finally {
      setLoading(false);
    }
  }, [getArticlesUseCase]);

  const fetchMoreArticles = useCallback(async () => {
    if (loadingMore || !hasMore.current || !lastVisibleArticleId.current) return;

    setLoadingMore(true);
    setError(null);
    try {
      const newArticles = await getArticlesUseCase.execute(ARTICLES_LIMIT, lastVisibleArticleId.current);
      setArticles((prev) => [...prev, ...newArticles]);
      lastVisibleArticleId.current = newArticles.length > 0 ? newArticles[newArticles.length - 1].id : null;
      hasMore.current = newArticles.length === ARTICLES_LIMIT;
    } catch (err) {
      console.error("Error fetching more articles:", err);
      setError("Failed to fetch more articles.");
    } finally {
      setLoadingMore(false);
    }
  }, [getArticlesUseCase, loadingMore]);

  const addArticle = useCallback(async (articleData) => {
    setError(null);
    try {
      const newArticle = await createArticleUseCase.execute(articleData);
      setArticles((prev) => [newArticle, ...prev]); // Add new article to the top
      return newArticle;
    } catch (err) {
      console.error("Error adding article:", err);
      setError("Failed to add article.");
      throw err; // Re-throw to allow UI to handle
    }
  }, [createArticleUseCase]);

  const updateArticle = useCallback(async (articleId, updates) => {
    setError(null);
    try {
      await updateArticleUseCase.execute(articleId, updates);
      setArticles((prev) =>
        prev.map((article) => (article.id === articleId ? { ...article, ...updates } : article))
      );
      return true;
    } catch (err) {
      console.error("Error updating article:", err);
      setError("Failed to update article.");
      throw err;
    }
  }, [updateArticleUseCase]);

  const deleteArticle = useCallback(async (articleId) => {
    setError(null);
    try {
      await deleteArticleUseCase.execute(articleId);
      setArticles((prev) => prev.filter((article) => article.id !== articleId));
      return true;
    } catch (err) {
      console.error("Error deleting article:", err);
      setError("Failed to delete article.");
      throw err;
    }
  }, [deleteArticleUseCase]);

  const getArticleDetails = useCallback(async (articleId) => {
    setError(null);
    try {
      const article = await getArticleByIdUseCase.execute(articleId);
      return article;
    } catch (err) {
      console.error("Error fetching article details:", err);
      setError("Failed to fetch article details.");
      throw err;
    }
  }, [getArticleByIdUseCase]);

  useEffect(() => {
    loadInitialArticles();
  }, [loadInitialArticles]);

  return {
    articles,
    loading,
    loadingMore,
    error,
    hasMore: hasMore.current,
    loadInitialArticles,
    fetchMoreArticles,
    addArticle,
    updateArticle,
    deleteArticle,
    getArticleDetails,
  };
};

export default useArticlesViewModel;
